// Library-sweep aggregation (§11 slow path). Enriches the user's rated films
// and derives the dashboard's enriched tier: genre distribution, top directors
// (by count and by average rating), and hot takes (rating vs the world).

import type { DistilledProfile } from "./types.ts";
import { enrichFilm, mockMeta, type TmdbMeta } from "./tmdb.ts";

export interface DirectorStat {
  name: string;
  count: number;
  avgRating: number;
}

export interface HotTake {
  uri: string;
  title: string;
  year?: number;
  userRating: number; // 0.5..5
  worldRating: number; // voteAverage/2, 0..5
  gap: number; // userRating - worldRating
}

export interface EnrichedStats {
  genres: { name: string; count: number }[];
  topDirectorsByCount: DirectorStat[];
  topDirectorsByRating: DirectorStat[];
  lovedByYouOnly: HotTake[]; // you loved it, world shrugged (gap > 0)
  lovedByWorldOnly: HotTake[]; // everyone loves it, you don't (gap < 0)
  /** Most-watched genre, or null. */
  signatureGenre: string | null;
  /** Mean absolute distance from the crowd across rated films (0..5). */
  contrarianIndex: number;
}

/** Minimum world vote-count to include a film in hot takes (avoids obscure noise). */
const MIN_VOTE_COUNT = 100;

export interface EnrichOptions {
  /** Inject a synchronous meta source (tests); defaults to the real cache. */
  metaFor?: (uri: string, title: string, year?: number) => Promise<TmdbMeta | null>;
  onProgress?: (done: number, total: number) => void;
}

export async function computeEnrichedStats(
  profile: DistilledProfile,
  opts: EnrichOptions = {},
): Promise<EnrichedStats> {
  const metaFor = opts.metaFor ?? enrichFilm;

  const rated = Object.entries(profile.films).filter(
    ([, f]) => f.s.includes("r") && typeof f.r === "number",
  );

  const genreCount = new Map<string, number>();
  const dirAgg = new Map<string, { count: number; sum: number }>();
  const hotTakes: HotTake[] = [];

  let done = 0;
  for (const [uri, f] of rated) {
    const meta = await metaFor(uri, f.t, f.y);
    done++;
    opts.onProgress?.(done, rated.length);
    if (!meta) continue;

    for (const g of meta.genres) genreCount.set(g, (genreCount.get(g) ?? 0) + 1);

    for (const d of meta.directors) {
      const cur = dirAgg.get(d) ?? { count: 0, sum: 0 };
      cur.count++;
      cur.sum += f.r as number;
      dirAgg.set(d, cur);
    }

    if (meta.voteCount >= MIN_VOTE_COUNT) {
      const worldRating = meta.voteAverage / 2;
      hotTakes.push({
        uri,
        title: f.t,
        year: f.y,
        userRating: f.r as number,
        worldRating: Math.round(worldRating * 100) / 100,
        gap: Math.round(((f.r as number) - worldRating) * 100) / 100,
      });
    }
  }

  const genres = [...genreCount.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || (a.name < b.name ? -1 : 1));

  const directors: DirectorStat[] = [...dirAgg.entries()].map(([name, v]) => ({
    name,
    count: v.count,
    avgRating: Math.round((v.sum / v.count) * 100) / 100,
  }));

  const topDirectorsByCount = [...directors]
    .sort((a, b) => b.count - a.count || b.avgRating - a.avgRating)
    .slice(0, 5);
  const topDirectorsByRating = [...directors]
    .filter((d) => d.count >= 2)
    .sort((a, b) => b.avgRating - a.avgRating || b.count - a.count)
    .slice(0, 5);

  const sortedByGap = [...hotTakes].sort((a, b) => b.gap - a.gap);
  const lovedByYouOnly = sortedByGap.filter((h) => h.gap > 0).slice(0, 5);
  const lovedByWorldOnly = sortedByGap
    .filter((h) => h.gap < 0)
    .sort((a, b) => a.gap - b.gap)
    .slice(0, 5);

  const contrarianIndex =
    hotTakes.length === 0
      ? 0
      : Math.round((hotTakes.reduce((s, h) => s + Math.abs(h.gap), 0) / hotTakes.length) * 100) / 100;
  const signatureGenre = genres[0]?.name ?? null;

  return {
    genres,
    topDirectorsByCount,
    topDirectorsByRating,
    lovedByYouOnly,
    lovedByWorldOnly,
    signatureGenre,
    contrarianIndex,
  };
}

/** Synchronous mock meta source for tests / deterministic rendering. */
export const mockMetaFor = async (uri: string, title: string): Promise<TmdbMeta> => mockMeta(uri, title);
